import { memo, useMemo, useState } from 'react';
import { Badge, Grid } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { formatMoneyString } from '../../../utils/strings';
import Table from '../../../components/Table';
import { ServicesToDeliverDetailItem } from '.';
import moment from 'moment';
import { MRT_PaginationState } from 'mantine-react-table';

const statusColor = (s: string) => {
  switch ((s || '').toLowerCase()) {
    case 'created':
      return 'gray';
    case 'cleaning':
      return 'blue';
    case 'ready':
      return 'green';
    default:
      return 'gray';
  }
};

export const buildColumns = (t: ReturnType<typeof useTranslation>['t']) => [
  {
    id: 'storeName',
    accessorFn: (row: ServicesToDeliverDetailItem) => row.storeName || '-',
    header: t('FieldName:store') || 'Sucursal'
  },
  {
    id: 'deliveryDate',
    accessorFn: (row: ServicesToDeliverDetailItem) =>
      row.deliveryDate ? moment(row.deliveryDate).format('DD/MM/YYYY') : '-',
    header: t('FieldName:deliveryDate') || 'Fecha entrega'
  },
  {
    id: 'folio',
    accessorFn: (row: ServicesToDeliverDetailItem) => row.folio || '-',
    header: t('FieldName:order') || 'Folio'
  },
  {
    id: 'customerName',
    accessorFn: (row: ServicesToDeliverDetailItem) => row.customerName || '-',
    header: t('FieldName:customer') || 'Cliente'
  },
  {
    id: 'displayName',
    accessorFn: (row: ServicesToDeliverDetailItem) => row.displayName || '-',
    header: t('FieldName:product') || 'Sección / Producto'
  },
  {
    id: 'quantity',
    accessorFn: (row: ServicesToDeliverDetailItem) => {
      const n = row.quantity || 0;
      const qty = Number.isInteger(n) ? String(n) : n.toFixed(2);
      let label = '';
      if (row.productType === 'weight') {
        label = row.unitLabel === 'lb' ? 'lbs' : 'kgs';
      } else if (row.productType === 'length') {
        label = row.unitLabel === 'ft' ? 'ft' : 'm';
      } else {
        label = t('FieldName:pieces');
      }
      return `${qty} ${label}`;
    },
    header: t('FieldName:quantity') || 'Cantidad'
  },
  {
    id: 'lineStatus',
    accessorFn: (row: ServicesToDeliverDetailItem) => row.lineStatus,
    header: t('FieldName:status') || 'Estatus',
    Cell: ({ cell }: any) => {
      const v = cell.getValue<string>() || '';
      return (
        <Badge color={statusColor(v)} variant="light">
          {t(`FieldName:orderStatuses.${v}`, { defaultValue: v })}
        </Badge>
      );
    }
  },
  {
    id: 'customerPhone',
    accessorFn: (row: ServicesToDeliverDetailItem) => row.customerPhone || '-',
    header: t('FieldName:phone') || 'Teléfono'
  },
  {
    id: 'total',
    accessorFn: (row: ServicesToDeliverDetailItem) => formatMoneyString((row.total || 0).toFixed(2)),
    header: t('FieldName:total') || 'Total pedido'
  },
{
    id: 'balance',
    accessorFn: (row: ServicesToDeliverDetailItem) =>
      formatMoneyString((row.balance || 0).toFixed(2)),
    header: t('FieldName:amountDue') || 'Saldo'
  }
];

export default memo(function TableView({
  tableInfoData = [],
  totalRowsCount = 0,
  isError = false,
  isLoading = false,
  isMultiStore = false,
  columnVisibility: columnVisibilityProp,
  setColumnVisibility: setColumnVisibilityProp
}: {
  tableInfoData: ServicesToDeliverDetailItem[];
  totalRowsCount: number;
  isError: boolean;
  isLoading: boolean;
  isMultiStore?: boolean;
  columnVisibility?: Record<string, boolean>;
  setColumnVisibility?: (
    updater:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => void;
}) {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const { t } = useTranslation(['FieldName', 'CommonButtons']);
  const [localColumnVisibility, setLocalColumnVisibility] = useState<Record<string, boolean>>({
    storeName: isMultiStore,
    customerPhone: false,
    total: false,
    balance: false
  });
  const columnVisibility = columnVisibilityProp ?? localColumnVisibility;
  const setColumnVisibility = setColumnVisibilityProp ?? setLocalColumnVisibility;

  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <Grid gutter="md">
      <Grid.Col span={12}>
        <Table
          tableColumns={columns}
          tableData={tableInfoData}
          isError={isError}
          paginationIsLoading={isLoading}
          totalRowsCount={totalRowsCount}
          overrideTableConfigs={{
            enablePagination: true,
            manualPagination: false,
            enableColumnActions: false,
            enableHiding: true,
            state: { columnVisibility },
            onColumnVisibilityChange: setColumnVisibility
          }}
          pagination={pagination}
          setPagination={setPagination}
          numPages={Math.ceil(totalRowsCount / pagination.pageSize)}
        />
      </Grid.Col>
    </Grid>
  );
});
